import type { NestedFilter } from '@@partner:types:graphql'
import type { UniversalSearchVariables } from '@@partner:graphql/query/universalSearch.gql'
import { generatePath } from 'react-router-dom'
import { ROUTE_CUSTOMERS_VIEW_CUSTOMER, ROUTE_LOGISTIC_SINGLE_COURIER, ROUTE_ORDERS_VIEW_ORDER, ROUTE_USERS_MANAGEMENT_EDIT, ROUTE_USERS_MANAGEMENT_VIEW_ASSEMBLER } from '@@partner:config/routes'
import { ORDER } from '@hive/status'

/**
 * Определяет, является ли строка числом (ID заказа)
 */

export const isNumericSearch = (query: string): boolean => {
  return /^\d+$/.test(query.trim())
}

/**
 * Максимальное значение для integer в PostgreSQL
 */

const MAX_INTEGER = 2147483647

/**
 * Определяет, является ли строка номером телефона
 * (начинается с "+" или это число больше MAX_INTEGER)
 */

export const isPhoneSearch = (query: string): boolean => {
  const trimmed = query.trim()
  
  // Начинается с "+"
  if (trimmed.startsWith('+')) {
    return true
  }
  
  // Число, которое превышает максимальный integer
  if (isNumericSearch(trimmed)) {
    const numValue = parseInt(trimmed, 10)
    if (numValue > MAX_INTEGER) {
      return true
    }
  }
  
  return false
}

/**
 * Создает фильтры для универсального поиска
 */

export const buildUniversalSearchFilters = (
  searchQuery: string,
  brandId?: string | number
): UniversalSearchVariables => {
  const trimmedQuery = searchQuery.trim()
  const isNumeric = isNumericSearch(trimmedQuery)
  const isPhone = isPhoneSearch(trimmedQuery)

  // Поиск по номеру телефона - ищем сотрудников, покупателей и заказы по телефону
  if (isPhone) {
    // Убираем "+" для поиска, если есть
    const phoneQuery = trimmedQuery.startsWith('+') ? trimmedQuery.slice(1) : trimmedQuery

    const phoneSearchFilter: NestedFilter = {
      op: 'includes',
      fields: [ 'phone' ],
      value: `%${phoneQuery}%`
    }

    // Поиск заказов по телефону клиента
    const orderPhoneFilter: NestedFilter = {
      op: 'includes',
      fields: [ 'customer_phone' ],
      value: `%${phoneQuery}%`
    }

    return {
      customerFilter: phoneSearchFilter,
      orderFilter: orderPhoneFilter,
      employeeFilter: phoneSearchFilter
    }
  }

  if (isNumeric) {
    // Поиск по числу - ищем заказы по ID и клиентов/сотрудников по ID или телефону
    const idFilter: NestedFilter = {
      op: 'eq',
      fields: [ 'id' ],
      value: trimmedQuery
    }

    // Фильтр для клиентов и сотрудников: по ID или по вхождению в телефон
    const idOrPhoneFilter: NestedFilter = {
      or: [
        {
          op: 'eq',
          fields: [ 'id' ],
          value: trimmedQuery
        },
        {
          op: 'includes',
          fields: [ 'phone' ],
          value: `%${trimmedQuery}%`
        }
      ]
    }

    return {
      customerFilter: idOrPhoneFilter,
      orderFilter: idFilter,
      employeeFilter: idOrPhoneFilter
    }
  }

  // Поиск по тексту - ищем по имени, фамилии, телефону
  const textSearchFilter: NestedFilter = {
    or: [
      {
        op: 'includes',
        fields: [ 'first_name' ],
        value: `%${trimmedQuery}%`
      },
      {
        op: 'includes',
        fields: [ 'last_name' ],
        value: `%${trimmedQuery}%`
      },
      {
        op: 'includes',
        fields: [ 'phone' ],
        value: `%${trimmedQuery}%`
      }
    ]
  }

  // Фильтр для сотрудников с учетом бренда
  const employeeFilter = textSearchFilter

  // Фильтр для покупателей (только текстовый поиск)
  const customerFilter: NestedFilter = textSearchFilter

  // Фильтр для заказов по имени клиента или телефону
  const orderSearchFilter: NestedFilter = {
    or: [
      {
        op: 'includes',
        fields: [ 'fullName' ],
        value: `%${trimmedQuery}%`
      },
      {
        op: 'includes',
        fields: [ 'customer_phone' ],
        value: `%${trimmedQuery}%`
      }
    ]
  }

  const orderFilter: NestedFilter = {
    and: [
      {
        fields: [ 'status' ],
        op: 'not_eq',
        value: ORDER.INITIAL
      },
      orderSearchFilter
    ]
  }

  return {
    customerFilter,
    orderFilter,
    employeeFilter
  }
}

/**
 * Форматирует результаты поиска для отображения
 */

export interface SearchResultItem {
  id: string | number
  type: 'customer' | 'order' | 'courier' | 'assembler' | 'supervisor' | 'operator'
  title: string
  subtitle?: string
  url?: string
  groupLabel?: string
}

export const getTypeLabel = (type: SearchResultItem['type']): string => {
  const labels = {
    customer: 'Покупатели',
    order: 'Заказы',
    courier: 'Курьеры',
    assembler: 'Сборщики',
    supervisor: 'Супервайзеры',
    operator: 'Операторы'
  }
  return labels[type]
}

export const formatSearchResults = (
  results: any,
  brandSlug: string
): SearchResultItem[] => {
  const items: SearchResultItem[] = []

  // Покупатели
  if (results.customers?.edges?.length) {
    results.customers.edges.forEach((customer: any) => {
      items.push({
        id: customer.id,
        type: 'customer',
        title: customer.fullName || `${customer.first_name} ${customer.last_name}`.trim() || 'Без имени',
        subtitle: customer.phone,
        url: generatePath(ROUTE_CUSTOMERS_VIEW_CUSTOMER, { brandSlug, customerId: customer.id })
      })
    })
  }

  // Заказы
  if (results.orders?.edges?.length) {
    results.orders.edges.forEach((order: any) => {
      items.push({
        id: order.id,
        type: 'order',
        title: `Заказ #${order.id}`,
        subtitle: order.fullName || order.customer?.fullName || order.customer_phone,
        url: generatePath(ROUTE_ORDERS_VIEW_ORDER, { orderId: order.id, brandSlug })
      })
    })
  }

  // Курьеры
  if (results.couriers?.edges?.length) {
    results.couriers.edges.forEach((courier: any) => {
      items.push({
        id: courier.id,
        type: 'courier',
        title: `Курьер: ${courier.fullName || `${courier.first_name} ${courier.last_name}`.trim()}`,
        subtitle: courier.phone,
        url: generatePath(ROUTE_USERS_MANAGEMENT_EDIT, { type: 'courier', id: courier.id })
      })
    })
  }

  // Сборщики
  if (results.assemblers?.edges?.length) {
    results.assemblers.edges.forEach((assembler: any) => {
      items.push({
        id: assembler.id,
        type: 'assembler',
        title: `Сборщик: ${assembler.fullName || `${assembler.first_name} ${assembler.last_name}`.trim()}`,
        subtitle: assembler.phone,
        url: generatePath(ROUTE_USERS_MANAGEMENT_EDIT, { type: 'assembler', id: assembler.id })
      })
    })
  }

  // Супервайзеры
  if (results.supervisors?.edges?.length) {
    results.supervisors.edges.forEach((supervisor: any) => {
      items.push({
        id: supervisor.id,
        type: 'supervisor',
        title: `Супервайзер: ${supervisor.fullName || `${supervisor.first_name} ${supervisor.last_name}`.trim()}`,
        subtitle: supervisor.phone || supervisor.email,
        url: generatePath(ROUTE_USERS_MANAGEMENT_EDIT, { type: 'supervisor', id: supervisor.id })
      })
    })
  }

  // Операторы
  if (results.operators?.edges?.length) {
    results.operators.edges.forEach((operator: any) => {
      items.push({
        id: operator.id,
        type: 'operator',
        title: `Оператор: ${operator.fullName || `${operator.first_name} ${operator.last_name}`.trim()}`,
        subtitle: operator.phone || operator.email
      })
    })
  }

  return items
}
